import { useCallback, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { customersAdminApi } from "../api/customersAdminApi";
import { useBrand } from "../components/brand/BrandProvider";
import { IMPERSONATE_RETURN_KEY } from "../components/layout/TopBar";
import { useSession } from "./useSession";

/**
 * "Open as customer": impersonate a customer (same server flow as the
 * admin customers list) and land on one of its pages. Remembers the
 * current page so TopBar's "Switch back" returns here instead of the
 * customers list.
 */
export function useOpenAsCustomer() {
  const navigate = useNavigate();
  const location = useLocation();
  const { setSession } = useSession();
  const { refresh: refreshBrand } = useBrand();
  const [switching, setSwitching] = useState<number | null>(null);
  const [error, setError] = useState<string | null>(null);

  const open = useCallback(
    async (customerId: number, path: string) => {
      setError(null);
      setSwitching(customerId);
      try {
        sessionStorage.setItem(IMPERSONATE_RETURN_KEY, location.pathname + location.search);
      } catch {
        // storage unavailable: switch back lands on the customers list
      }
      try {
        const newSession = await customersAdminApi.switchTo(customerId);
        setSession(newSession?.isAuthenticated ? newSession : null);
        try {
          await refreshBrand();
        } catch {
          // brand refresh is cosmetic
        }
        navigate(path);
      } catch (err) {
        setError(err instanceof Error ? err.message : "Failed to open as customer");
        setSwitching(null);
      }
    },
    [location.pathname, location.search, navigate, refreshBrand, setSession],
  );

  return { open, switching, error };
}
